import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

const CATEGORIES = ["All", "Necklaces", "Earrings", "Rings", "Bracelets"];
const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price_asc", label: "Price: Low to High" },
  { id: "price_desc", label: "Price: High to Low" },
  { id: "newest", label: "Newest" },
];

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const category = params.get("category") || "All";
  const sort = params.get("sort") || "featured";

  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setProducts(null);
    const q = { sort };
    if (category && category !== "All") q.category = category;
    if (query) q.search = query;
    api.get("/products", { params: q })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, [category, sort, query]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category"); else next.set("category", c);
    setParams(next, { replace: true });
  };

  const setSort = (s) => {
    const next = new URLSearchParams(params);
    next.set("sort", s);
    setParams(next, { replace: true });
  };

  return (
    <main className="pt-[68px] min-h-screen" data-testid="shop-page">
      <section className="border-b border-[#d4af37]/12 bg-[#0c0c0e]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <Reveal>
            <p className="eyebrow mb-4">The Collection</p>
            <h1 className="font-serif font-light text-4xl md:text-6xl text-zinc-100">
              Shop <span className="italic text-gold-gradient">Jwellary</span>
            </h1>
            <p className="mt-4 text-zinc-500 text-sm max-w-lg">
              Every piece crafted in brass, plated in 2-micron 18k gold, finished by hand.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between mb-10">
          <div className="flex flex-wrap gap-2" data-testid="shop-category-chips">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`shop-chip-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`px-5 py-2.5 rounded-full text-xs tracking-[0.14em] uppercase border transition-all duration-300 ${
                  category === c
                    ? "bg-[#d4af37] text-[#0a0a0b] border-[#d4af37] font-semibold"
                    : "border-[#d4af37]/25 text-zinc-400 hover:border-[#d4af37]/70 hover:text-[#f3e5ab]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <div className="relative flex-1 lg:w-60">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search pieces..."
                className="input-dark pl-10 py-2.5"
                data-testid="shop-search-input"
                aria-label="Search products"
              />
            </div>
            <div className="relative">
              <SlidersHorizontal size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="input-dark pl-10 pr-8 py-2.5 appearance-none cursor-pointer"
                data-testid="shop-sort-select"
                aria-label="Sort products"
              >
                {SORTS.map((s) => (
                  <option key={s.id} value={s.id} className="bg-[#121215]">{s.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {products === null ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7" data-testid="shop-loading">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-xl bg-[#121215] aspect-[4/5] shimmer bg-gradient-to-r from-[#121215] via-[#1c1c20] to-[#121215] bg-[length:200%_100%] animate-shimmer" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="py-24 text-center" data-testid="shop-empty-state">
            <p className="font-serif text-3xl text-zinc-300">No pieces found</p>
            <p className="text-sm text-zinc-500 mt-2">Try a different search or category.</p>
            <button
              onClick={() => { setSearch(""); setCategory("All"); }}
              className="btn-ghost mt-6"
              data-testid="shop-reset-filters-button"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7 pb-24" data-testid="shop-grid">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>
    </main>
  );
};

export default Shop;
